import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import StoreLayout from "../components/StoreLayout";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";

function Confetti() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const ctx = canvas.getContext("2d");
    const COLORS = ["#CFF048", "#FFFFFF", "#FFD700", "#FF6B6B", "#4ECDC4", "#A855F7"];
    const pieces = Array.from({ length: 120 }, () => ({
      x: Math.random() * canvas.width,
      y: -20 - Math.random() * 100,
      r: 5 + Math.random() * 7,
      speed: 2 + Math.random() * 3,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      tilt: Math.random() * 12 - 6,
      tiltSpeed: 0.05 + Math.random() * 0.05,
      shape: Math.random() > 0.5 ? "rect" : "circle",
    }));
    let raf;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      pieces.forEach((p) => {
        ctx.beginPath();
        ctx.fillStyle = p.color;
        if (p.shape === "circle") {
          ctx.arc(p.x, p.y, p.r / 2, 0, Math.PI * 2);
        } else {
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.tilt);
          ctx.fillRect(-p.r / 2, -p.r, p.r, p.r * 2);
          ctx.restore();
        }
        ctx.fill();
        p.y += p.speed;
        p.tilt += p.tiltSpeed;
        p.x += Math.sin(p.tilt) * 0.8;
        if (p.y > canvas.height + 10) {
          p.y = -20;
          p.x = Math.random() * canvas.width;
        }
      });
      raf = requestAnimationFrame(draw);
    };
    draw();
    const stop = setTimeout(() => cancelAnimationFrame(raf), 4500);
    return () => { cancelAnimationFrame(raf); clearTimeout(stop); };
  }, []);
  return <canvas ref={ref} className="fixed inset-0 pointer-events-none z-50" />;
}

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const orderId = params.get("order_id");
  const [status, setStatus] = useState("pending"); // pending | paid | expired | error
  const polled = useRef(0);
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          clear();
          return;
        }
        if (res.data.status === "expired") { setStatus("expired"); return; }
        if (polled.current++ < 10) setTimeout(poll, 2000);
        else setStatus("error");
      } catch {
        if (polled.current++ < 10) setTimeout(poll, 2000);
        else setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <StoreLayout>
      {status === "paid" && <Confetti />}
      <div className="max-w-2xl mx-auto py-20 px-4 text-center" data-testid="payment-success-page">
        {status === "pending" && (
          <>
            <Loader2 size={64} className="text-[#CFF048] mx-auto animate-spin" />
            <p className="label-mono text-[#CFF048] mt-6">// PROCESSING</p>
            <h1 className="font-display font-black text-4xl uppercase mt-3">Hold tight...</h1>
            <p className="text-[#A1A1AA] mt-3 term-loader">Verifying payment</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="relative inline-block">
              <CheckCircle2 size={72} className="text-[#CFF048] mx-auto animate-bounce" />
            </div>
            <p className="label-mono text-[#CFF048] mt-6 tracking-widest animate-pulse">// ORDER PLACED — YOU&apos;RE IN THE CULT</p>
            <h1 className="font-display font-black text-5xl md:text-6xl uppercase mt-3 leading-none">Welcome to<br />the Cult.</h1>
            <p className="text-[#A1A1AA] mt-4 text-lg">Your order is in. We&apos;ll print and ship it from the underground.</p>
            <p className="label-mono text-[#71717A] mt-2 text-sm">// CHECK YOUR EMAIL FOR YOUR OTP LOGIN CODE + ORDER CONFIRMATION</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-10">
              <Link to={`/orders/${orderId}`} className="btn-neon" data-testid="track-order-btn">TRACK MY ORDER</Link>
              <Link to="/shop" className="btn-outline">KEEP SHOPPING</Link>
            </div>
          </>
        )}
        {(status === "expired" || status === "error") && (
          <>
            <XCircle size={64} className="text-red-500 mx-auto" />
            <p className="label-mono text-red-500 mt-6">// PAYMENT NOT COMPLETED</p>
            <h1 className="font-display font-black text-4xl uppercase mt-3">Something went wrong</h1>
            <Link to="/cart" className="btn-outline mt-6 inline-flex">BACK TO CART</Link>
          </>
        )}
      </div>
    </StoreLayout>
  );
}
